/**
 * What ONE device remembers about how a person likes the portal.
 *
 * Small, per-phone conveniences only: which sections a dispatcher keeps open
 * (CollapsibleSection) and which one-time offers they have waved away
 * (PushAsk, AddToHomeOffer). Never anything about the person, the session or
 * a trip — those live on the server, and this survives a sign-out.
 *
 * STORAGE IS ALWAYS ALLOWED TO FAIL. Private browsing, cleared site data, a
 * quota, an iOS Home Screen app with its own empty storage: every read and
 * write is wrapped, and the answer when it fails is the DEFAULT — a fold
 * opens as it would on a fresh phone, an offer is shown again. Remembering
 * is a convenience, never a requirement, and nothing here may throw into a
 * render.
 *
 * Each store is one JSON object under one key, re-read before every write,
 * so two things writing on the same screen cannot overwrite each other.
 */

/** Which sections are open. The dispatcher's folds have always lived here. */
export const FOLD_STORE = 'ph_portal_folds';

/** One-time offers this device has been shown and has waved away. */
export const DISMISSED_STORE = 'ph_portal_dismissed';

/** Everything remembered in one store, or `{}` when storage is unavailable. */
export const readRemembered = (store) => {
  try {
    return JSON.parse(window.localStorage.getItem(store)) || {};
  } catch (e) {
    return {};
  }
};

/** One remembered value, or `undefined` when there is none. */
export const rememberedValue = (store, key) => readRemembered(store)[key];

/** Remember one value, leaving everything else in the store alone. */
export const remember = (store, key, value) => {
  try {
    window.localStorage.setItem(store, JSON.stringify({ ...readRemembered(store), [key]: value }));
  } catch (e) {
    // Ignore — see the note at the top.
  }
};

/** Has this device waved away the offer called `key`? */
export const isDismissed = (key) => rememberedValue(DISMISSED_STORE, key) === true;

/** Wave it away, for good, on this device. */
export const dismissOffer = (key) => remember(DISMISSED_STORE, key, true);
